import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Star, X, Upload, Loader2 } from "lucide-react";
import { DateTimePicker } from "@/components/ui/DateTimePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useFeedback } from "@/hooks/useFeedback";
import { useLanguage } from "@/context/LanguageContext";
import { dashboardLabels } from "@/i18n";
import type { BugReportDeviceType, BugReportOs } from "@/services/feedback/feedbackService";
import { DeviceIcon, OsIcon } from "@/components/feedback/BugReportDeviceOsIcons";
import { MediaService } from "@/services/media/mediaService";
import { compressImageForPreview, compressImageForFullSize } from "@/utils/imageCompression";
import { isIOS } from "@/utils/platformUtils";
import { FileUploadTrigger } from "@/components/native/FileUploadTrigger";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { cn } from "@/lib/utils";

const MAX_DESCRIPTION = 500;
const MAX_IMAGES = 5;

const DEVICE_KEYS: { value: BugReportDeviceType; key: string }[] = [
  { value: "MOBILE", key: "deviceMobile" },
  { value: "TABLET", key: "deviceTablet" },
  { value: "DESKTOP", key: "deviceDesktop" },
];

const OS_KEYS: { value: BugReportOs; key: string }[] = [
  { value: "ANDROID", key: "osAndroid" },
  { value: "IOS", key: "osIos" },
  { value: "WINDOWS", key: "osWindows" },
];

const DEVICE_ICON_CLASS = "h-4 w-4 shrink-0";

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTab?: "feedback" | "bug";
}

export const FeedbackDialog = ({ open, onOpenChange, initialTab = "feedback" }: FeedbackDialogProps) => {
  const { language } = useLanguage();
  const { isNative } = useNativePlatform();
  const fbLabels = (dashboardLabels(language)?.feedback || {}) as Record<string, string>;
  const {
    submitFeedbackAsync,
    submitBugReportAsync,
    isSubmittingFeedback,
    isSubmittingBugReport,
  } = useFeedback();

  const [activeTab, setActiveTab] = useState<"feedback" | "bug">(initialTab);

  useEffect(() => {
    if (open) setActiveTab(initialTab);
  }, [open, initialTab]);

  const [feedbackStars, setFeedbackStars] = useState(0);
  const [feedbackHover, setFeedbackHover] = useState(0);
  const [feedbackDescription, setFeedbackDescription] = useState("");
  const [feedbackFiles, setFeedbackFiles] = useState<File[]>([]);

  const [bugDescription, setBugDescription] = useState("");
  const [bugOccurredAt, setBugOccurredAt] = useState(
    () => new Date().toISOString()
  );
  const [bugDeviceType, setBugDeviceType] = useState<BugReportDeviceType | "">("");
  const [bugOs, setBugOs] = useState<BugReportOs | "">("");
  const [bugFiles, setBugFiles] = useState<File[]>([]);

  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);
  const isSubmitting =
    isSubmittingLocal || isSubmittingFeedback || isSubmittingBugReport;

  const DEVICE_TYPES = DEVICE_KEYS.map((d) => ({ value: d.value, label: fbLabels[d.key] || d.value }));
  const OS_OPTIONS = OS_KEYS.map((o) => ({ value: o.value, label: fbLabels[o.key] || o.value }));

  const resetForm = () => {
    setFeedbackStars(0);
    setFeedbackHover(0);
    setFeedbackDescription("");
    setFeedbackFiles([]);
    setBugDescription("");
    setBugOccurredAt(new Date().toISOString());
    setBugDeviceType("");
    setBugOs("");
    setBugFiles([]);
  };

  const handleFeedbackSubmit = async () => {
    if (feedbackStars < 1 || feedbackStars > 5) {
      toast.error(fbLabels.toastSelectStars || "Välj 1–5 stjärnor");
      return;
    }
    if (feedbackDescription.length > MAX_DESCRIPTION) {
      toast.error((fbLabels.toastDescriptionMax || "Beskrivning max {max} tecken").replace("{max}", String(MAX_DESCRIPTION)));
      return;
    }
    setIsSubmittingLocal(true);
    try {
      const { id } = await submitFeedbackAsync({
        description: feedbackDescription.slice(0, MAX_DESCRIPTION),
        stars: feedbackStars,
      });
      if (feedbackFiles.length > 0) {
        for (const file of feedbackFiles.slice(0, MAX_IMAGES)) {
          let previewFile: File;
          let fullSizeFile: File;
          try {
            previewFile = await compressImageForPreview(file);
            fullSizeFile = await compressImageForFullSize(file);
          } catch {
            previewFile = file;
            fullSizeFile = file;
          }
          const ext = file.name.split(".").pop() || "jpg";
          const baseName = file.name.replace(/\.[^/.]+$/, "");
          const previewFileName = `${baseName}_preview.${ext}`;
          const fullSizeFileName = `${baseName}_fullsize.${ext}`;

          const { uploadUrl: previewUploadUrl, s3Key: previewS3Key } =
            await MediaService.requestPresignedUploadUrl(
              "feedback",
              id,
              {
                originalName: previewFileName,
                mimeType: previewFile.type,
                sizeBytes: previewFile.size,
              },
              "preview"
            );
          await MediaService.uploadFileToS3(previewUploadUrl, previewFile);
          const previewMedia = await MediaService.createMediaAfterUpload(
            previewS3Key,
            {
              originalName: previewFileName,
              mimeType: previewFile.type,
              sizeBytes: previewFile.size,
            },
            { feedbackId: id, mediaSize: "PREVIEW" }
          );
          await MediaService.updateMediaStatus(previewMedia.id, "UPLOADED");

          const { uploadUrl: fullSizeUploadUrl, s3Key: fullSizeS3Key } =
            await MediaService.requestPresignedUploadUrl(
              "feedback",
              id,
              {
                originalName: fullSizeFileName,
                mimeType: fullSizeFile.type,
                sizeBytes: fullSizeFile.size,
              },
              "fullsize"
            );
          await MediaService.uploadFileToS3(fullSizeUploadUrl, fullSizeFile);
          const fullSizeMedia = await MediaService.createMediaAfterUpload(
            fullSizeS3Key,
            {
              originalName: fullSizeFileName,
              mimeType: fullSizeFile.type,
              sizeBytes: fullSizeFile.size,
            },
            { feedbackId: id, mediaSize: "FULL_SIZE" }
          );
          await MediaService.updateMediaStatus(fullSizeMedia.id, "UPLOADED");
        }
      }
      toast.success(fbLabels.toastFeedbackSent ?? (language === "en" ? "Thanks! Your feedback has been sent." : "Tack! Din feedback har skickats."));
      resetForm();
      onOpenChange(false);
    } catch (e) {
      toast.error(fbLabels.toastFeedbackError || "Kunde inte skicka feedback. Försök igen.");
    } finally {
      setIsSubmittingLocal(false);
    }
  };

  const handleBugSubmit = async () => {
    if (!bugDeviceType || !bugOs) {
      toast.error(fbLabels.toastFillDeviceOs || "Fyll i enhet och operativsystem");
      return;
    }
    if (!bugDescription.trim()) {
      toast.error(fbLabels.toastDescriptionRequired || "Beskrivning krävs");
      return;
    }
    if (bugDescription.length > MAX_DESCRIPTION) {
      toast.error((fbLabels.toastDescriptionMax || "Beskrivning max {max} tecken").replace("{max}", String(MAX_DESCRIPTION)));
      return;
    }
    setIsSubmittingLocal(true);
    try {
      const { id } = await submitBugReportAsync({
        description: bugDescription.slice(0, MAX_DESCRIPTION),
        occurredAt: bugOccurredAt,
        deviceType: bugDeviceType,
        os: bugOs,
      });
      if (bugFiles.length > 0) {
        for (const file of bugFiles.slice(0, MAX_IMAGES)) {
          let previewFile: File;
          let fullSizeFile: File;
          try {
            previewFile = await compressImageForPreview(file);
            fullSizeFile = await compressImageForFullSize(file);
          } catch {
            previewFile = file;
            fullSizeFile = file;
          }
          const ext = file.name.split(".").pop() || "jpg";
          const baseName = file.name.replace(/\.[^/.]+$/, "");
          const previewFileName = `${baseName}_preview.${ext}`;
          const fullSizeFileName = `${baseName}_fullsize.${ext}`;

          const { uploadUrl: previewUploadUrl, s3Key: previewS3Key } =
            await MediaService.requestPresignedUploadUrl(
              "bugReport",
              id,
              {
                originalName: previewFileName,
                mimeType: previewFile.type,
                sizeBytes: previewFile.size,
              },
              "preview"
            );
          await MediaService.uploadFileToS3(previewUploadUrl, previewFile);
          const previewMedia = await MediaService.createMediaAfterUpload(
            previewS3Key,
            {
              originalName: previewFileName,
              mimeType: previewFile.type,
              sizeBytes: previewFile.size,
            },
            { bugReportId: id, mediaSize: "PREVIEW" }
          );
          await MediaService.updateMediaStatus(previewMedia.id, "UPLOADED");

          const { uploadUrl: fullSizeUploadUrl, s3Key: fullSizeS3Key } =
            await MediaService.requestPresignedUploadUrl(
              "bugReport",
              id,
              {
                originalName: fullSizeFileName,
                mimeType: fullSizeFile.type,
                sizeBytes: fullSizeFile.size,
              },
              "fullsize"
            );
          await MediaService.uploadFileToS3(fullSizeUploadUrl, fullSizeFile);
          const fullSizeMedia = await MediaService.createMediaAfterUpload(
            fullSizeS3Key,
            {
              originalName: fullSizeFileName,
              mimeType: fullSizeFile.type,
              sizeBytes: fullSizeFile.size,
            },
            { bugReportId: id, mediaSize: "FULL_SIZE" }
          );
          await MediaService.updateMediaStatus(fullSizeMedia.id, "UPLOADED");
        }
      }
      toast.success(fbLabels.toastBugSent ?? (language === "en" ? "Thanks! Your bug report has been sent." : "Tack! Din felrapport har skickats."));
      resetForm();
      onOpenChange(false);
    } catch (e) {
      toast.error(fbLabels.toastBugError || "Kunde inte skicka felrapport. Försök igen.");
    } finally {
      setIsSubmittingLocal(false);
    }
  };

  const addFeedbackFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const apply = () => {
      setFeedbackFiles((prev) => prev.concat(files).slice(0, MAX_IMAGES));
      e.target.value = "";
    };
    if (isIOS() && files.length > 0) {
      setTimeout(apply, 100);
    } else {
      apply();
    }
  };
  const addBugFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const apply = () => {
      setBugFiles((prev) => prev.concat(files).slice(0, MAX_IMAGES));
      e.target.value = "";
    };
    if (isIOS() && files.length > 0) {
      setTimeout(apply, 100);
    } else {
      apply();
    }
  };
  const removeFeedbackFile = (i: number) => {
    setFeedbackFiles((prev) => prev.filter((_, idx) => idx !== i));
  };
  const removeBugFile = (i: number) => {
    setBugFiles((prev) => prev.filter((_, idx) => idx !== i));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        sheetOpen={open}
        compact
        className={cn(
          "max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto my-4",
          isNative && "native-sheet-form",
        )}
      >
        <DialogHeader className="pb-4">
          <DialogTitle>
            {activeTab === "feedback" ? (fbLabels.dialogTitleFeedback || "Feedback") : (fbLabels.dialogTitleBug || "Buggar & Fel")}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground font-normal mt-1">
            {activeTab === "feedback"
              ? (fbLabels.subtitleFeedback || "Hjälp oss bli bättre!")
              : (fbLabels.subtitleBug || "Har du upptäckt något fel?")}
          </DialogDescription>
        </DialogHeader>
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as "feedback" | "bug")}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="feedback">{fbLabels.leaveFeedback || "Leave feedback"}</TabsTrigger>
            <TabsTrigger value="bug">{fbLabels.reportBug || "Report bug/issue"}</TabsTrigger>
          </TabsList>

          <TabsContent value="feedback" className="space-y-4 mt-4">
            <div>
              <Label>{fbLabels.ratingLabel || "Betyg (1–5 stjärnor)"}</Label>
              <div className="flex gap-1 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <motion.button
                    key={star}
                    type="button"
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.95 }}
                    onMouseEnter={() => setFeedbackHover(star)}
                    onMouseLeave={() => setFeedbackHover(0)}
                    onClick={() => setFeedbackStars(star)}
                    className="p-1 rounded focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    <Star
                      className={`h-8 w-8 transition-colors ${
                        star <= (feedbackHover || feedbackStars)
                          ? "fill-amber-400 text-amber-400"
                          : "text-muted-foreground"
                      }`}
                    />
                  </motion.button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="feedback-desc">{(fbLabels.descriptionLabel || "Beskrivning (max {max} tecken)").replace("{max}", String(MAX_DESCRIPTION))}</Label>
              <Textarea
                id="feedback-desc"
                value={feedbackDescription}
                onChange={(e) => setFeedbackDescription(e.target.value.slice(0, MAX_DESCRIPTION))}
                maxLength={MAX_DESCRIPTION}
                placeholder={fbLabels.descriptionPlaceholder || "Berätta gärna mer..."}
                rows={4}
                className="mt-1"
              />
              <span className="text-xs text-muted-foreground">
                {feedbackDescription.length}/{MAX_DESCRIPTION}
              </span>
            </div>
            <div>
              <Label>{(fbLabels.imagesLabel || "Bilder (max {max})").replace("{max}", String(MAX_IMAGES))}</Label>
              <FileUploadTrigger
                accept="image/*"
                multiple
                disabled={feedbackFiles.length >= MAX_IMAGES}
                onFilesSelected={(files) => {
                  const dt = new DataTransfer();
                  files.forEach((f) => dt.items.add(f));
                  addFeedbackFiles({
                    target: { files: dt.files },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
              >
                {(open) => (
                  <button
                    type="button"
                    disabled={feedbackFiles.length >= MAX_IMAGES}
                    aria-label={fbLabels.selectFromGallery || "Välj bilder från galleri"}
                    className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-4 mt-2 ml-4 ${feedbackFiles.length >= MAX_IMAGES ? "pointer-events-none opacity-50" : "cursor-pointer"}`}
                    onClick={open}
                  >
                    <Upload className="h-4 w-4" />
                    {fbLabels.uploadImages || "Ladda upp bilder"}
                  </button>
                )}
              </FileUploadTrigger>
              <p className="text-xs text-muted-foreground mt-1.5 ml-4">
                {fbLabels.selectFromGallery || "Välj bilder från galleri"}
              </p>
              {feedbackFiles.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {feedbackFiles.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <span className="truncate flex-1">{f.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => removeFeedbackFile(i)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Button
              onClick={handleFeedbackSubmit}
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {fbLabels.sending || "Sending..."}
                </>
              ) : (
                fbLabels.sendFeedback || "Send feedback"
              )}
            </Button>
          </TabsContent>

          <TabsContent value="bug" className="space-y-4 mt-4">
            <div>
              <Label>{fbLabels.timeOfError || "Time of error"}</Label>
              <div className="flex items-end gap-2 mt-1">
                <div className="flex-1">
                  <DateTimePicker
                    value={bugOccurredAt}
                    onChange={setBugOccurredAt}
                    label=""
                    allowPastDates
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setBugOccurredAt(new Date().toISOString())}
                >
                  {fbLabels.now || "Nu"}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{fbLabels.device || "Device"}</Label>
                <Select
                  value={bugDeviceType}
                  onValueChange={(v) => setBugDeviceType(v as BugReportDeviceType)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={fbLabels.selectPlaceholder || "Select"} />
                  </SelectTrigger>
                  <SelectContent>
                    {DEVICE_TYPES.map((d) => (
                      <SelectItem key={d.value} value={d.value}>
                        <span className="flex items-center gap-2">
                          <DeviceIcon value={d.value} className={DEVICE_ICON_CLASS} />
                          {d.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{fbLabels.os || "Operating system"}</Label>
                <Select value={bugOs} onValueChange={(v) => setBugOs(v as BugReportOs)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={fbLabels.selectPlaceholder || "Select"} />
                  </SelectTrigger>
                  <SelectContent>
                    {OS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        <span className="flex items-center gap-2">
                          <OsIcon value={o.value} className="h-4 w-4 shrink-0" />
                          {o.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="bug-desc">{(fbLabels.descriptionLabel || "Beskrivning (max {max} tecken)").replace("{max}", String(MAX_DESCRIPTION))}</Label>
              <Textarea
                id="bug-desc"
                value={bugDescription}
                onChange={(e) => setBugDescription(e.target.value.slice(0, MAX_DESCRIPTION))}
                maxLength={MAX_DESCRIPTION}
                placeholder={fbLabels.bugPlaceholder || "Beskriv vad som hände..."}
                rows={4}
                className="mt-1"
              />
              <span className="text-xs text-muted-foreground">
                {bugDescription.length}/{MAX_DESCRIPTION}
              </span>
            </div>
            <div>
              <Label>{(fbLabels.imagesLabel || "Bilder (max {max})").replace("{max}", String(MAX_IMAGES))}</Label>
              <FileUploadTrigger
                accept="image/*"
                multiple
                disabled={bugFiles.length >= MAX_IMAGES}
                onFilesSelected={(files) => {
                  const dt = new DataTransfer();
                  files.forEach((f) => dt.items.add(f));
                  addBugFiles({
                    target: { files: dt.files },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
              >
                {(open) => (
                  <button
                    type="button"
                    disabled={bugFiles.length >= MAX_IMAGES}
                    aria-label={fbLabels.selectFromGallery || "Välj bilder från galleri"}
                    className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-4 mt-2 ml-4 ${bugFiles.length >= MAX_IMAGES ? "pointer-events-none opacity-50" : "cursor-pointer"}`}
                    onClick={open}
                  >
                    <Upload className="h-4 w-4" />
                    {fbLabels.uploadImages || "Ladda upp bilder"}
                  </button>
                )}
              </FileUploadTrigger>
              <p className="text-xs text-muted-foreground mt-1.5 ml-4">
                {fbLabels.selectFromGallery || "Välj bilder från galleri"}
              </p>
              {bugFiles.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {bugFiles.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <span className="truncate flex-1">{f.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => removeBugFile(i)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Button
              onClick={handleBugSubmit}
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {fbLabels.sending || "Skickar..."}
                </>
              ) : (
                fbLabels.sendBugReport || "Skicka felrapport"
              )}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
